import React, { FC } from "react";
import { SectionHeading } from "../SectionHeading/SectionHeading";
import Carousel from "../Carousel/Carousel";
import fallbackImage from "../../../../../prod/images/bg3.jpg";
import { ButtonVariant } from "../../types/button";
import classNames from "classnames";
import Link from "../Link/Link";
import { OptimizelyLongString, OptimizelyNumber } from "../../types/optimizely/blocks";
import { getHeaderClassNameRedesign } from "../PagePreviewBlock/helpers";
import { RedesignHeaderType } from "../PagePreviewBlock/types";

export interface EmployeeStoryCard {
  title: string;
  link: string;
  image?: string;
  department: string;
}

export interface EmployeesBlockProps {
  title: OptimizelyLongString;
  boldTitle: OptimizelyLongString;
  headerType: OptimizelyNumber;
  sectionSubName: string;
  employeeStories: EmployeeStoryCard[];
  buttonText: string;
  buttonLink: string;
  buttonVariant: ButtonVariant;
  isFrontPage?: boolean;
  sectionId?: string;
}

export const EmployeesBlock: FC<EmployeesBlockProps> = ({
  employeeStories,
  title,
  boldTitle,
  headerType,
  sectionSubName,
  buttonText,
  buttonLink,
  buttonVariant,
  isFrontPage,
  sectionId,
}) => {
  const headerTypeValue = headerType?.value ?? RedesignHeaderType.Big;
  const shouldRenderBoldHeader = headerTypeValue === RedesignHeaderType.Big;

  return (
  <div id={sectionId || undefined}>
    {!!employeeStories.length && (
      <>
        <div className={classNames("flex justify-between items-end mb-14", { "container": !shouldRenderBoldHeader })}>
          <h2 className={getHeaderClassNameRedesign(headerTypeValue)}>
            {title?.value}
            {shouldRenderBoldHeader && (
              <span className="font-bold">
                {" " + boldTitle?.value}
              </span>
            )}
          </h2>
     
          {buttonLink && ( 
            <Link
            className="hidden lg:inline-flex max-h-10 mr-8 md:mr-[62px]"
            variant={"link-primary"}
            text={buttonText}
            href={buttonLink}
            />   
          )}
        </div>
      <div
        className={classNames(
        "pb-12 container",
        isFrontPage ? "pb-28 md:pb-32" : ""
      )}
      >

      {/* TODO: Add back in when we decide how header should look */}
      {/* <SectionHeading colorVariant="primary" title={sectionName} /> */}
      <h2 className="block md:hidden text-2xl text-primary ml-8 md:ml-[62px]">{sectionSubName}</h2>
        <Carousel>
          <>
            {employeeStories.map((employeeStory, index) => (
              <div
                className="flex-shrink-0 min-w-0 pl-5 first:pl-[10px] md:first:pl-0"
                key={index}
              >
                <div className="flex flex-col gap-4 max-w-72 h-full">
                  <div className="max-h-96">
                    <div className="inline-block py-1 px-2 rounded bg-secondaryYellowLight absolute mt-5 ml-4">
                      <p className="text-xs text-primary text-nowrap">
                        {employeeStory.department}
                      </p>
                    </div>

                    <img
                      loading="lazy"
                      decoding="async"
                      alt={"TODO alt"}
                      className="object-cover h-96 rounded w-full bg-secondaryLightGreen"
                      src={employeeStory?.image || fallbackImage}
                    />
                  </div>

                  <h5 className="text-primary font-serif text-[22px] leading-tight">
                    {employeeStory.title}
                  </h5>

                  <Link
                    text="Read more"
                    href={employeeStory.link}
                    variant={buttonVariant || "link-secondary"}
                    className="max-h-10 w-full mt-auto"
                    forceAddIcon={true}
                  />
                </div>
              </div>
            ))}
          </>
        </Carousel>


        {buttonText && (
          <Link
            text={buttonText}
            href={buttonLink}
            variant="link-primary"
            className="min-h-10 w-max lg:hidden flex mt-6"
            />
          )}
        </div>
      </>
    )}
  </div>
  );
};
